import * as React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { LabeledControl } from "@replo/design-system/blocks/labeled-control/LabeledControl";
import { Button } from "@replo/design-system/components/button/Button";
import { Input } from "@replo/design-system/components/input/Input";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import { useCurrentWorkspaceContext } from "@v2/contexts/WorkspaceDashboardContext";
import { useCanAddCustomDomainToShop } from "@v2/features/settings/hooks/useCanAddCustomDomainToShop";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { entriRouter } from "@v2/trpc/routes/entri";
import {
  v2CustomDomainRouter,
  v2CustomDomainUtils,
} from "@v2/trpc/routes/v2CustomDomain";
import { getFirstError } from "@v2/utils/form-error-handling";
import { useForm } from "react-hook-form";
import { isValidDomain as isValidDomainFormat } from "replo-utils/lib/url";
import { z } from "zod";

import { DomainsDisplay } from "./DomainsDisplay";
import showEntriModal from "./showEntriModal";

/**
 * Validate that a given domain is valid. A valid domain is either a
 * root url such as something.com or a subdomain such as www.something.com
 */
const baseDomainSchema = z.object({
  domain: z
    .string()
    .min(1, "Domain is required")
    .refine((d) => isValidDomainFormat(d), {
      message:
        "Please enter a valid domain (e.g., example.com or shop.example.com)",
    }),
});

type DomainFormData = z.infer<typeof baseDomainSchema>;

interface AddDomainFormProps {
  projectId: string;
  shopDomainPrefix: string;
  showCancelButton?: boolean;
  isEntriModalInProgress: boolean;
}

export const AddDomainForm: React.FC<AddDomainFormProps> = ({
  projectId,
  shopDomainPrefix,
  showCancelButton = true,
  isEntriModalInProgress,
}) => {
  const { workspace } = useCurrentWorkspaceContext();
  const logEvent = useLogAnalytics();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm<DomainFormData>({
    resolver: zodResolver(baseDomainSchema),
    mode: "onSubmit",
    defaultValues: {
      domain: "",
    },
  });

  // NOTE (Cole, 2025-06-02): JWT expires after an hour, may cause issues for edge case users
  const { data: entriData } = entriRouter.fetchTokenAndApplicationId.useQuery({
    projectId,
  });

  const { token: entriToken, applicationId } = entriData ?? {};

  const { canAdd: canAddCustomDomain, hasPaidSubscription } =
    useCanAddCustomDomainToShop({ projectId });

  const { mutate: createPendingDomain } =
    v2CustomDomainRouter.createPendingCustomDomain.useMutation({
      onSuccess: () => {
        // Invalidate the domains query to refetch and show the new pending domain
        void v2CustomDomainUtils.getByProjectId.invalidate({
          projectId,
        });
      },
    });

  if (!workspace) {
    return null;
  }

  /**
   * This is the beginning of the following flow:
   *
   * https://www.figma.com/board/h2XJFTgZxyO9aRR9vwOTTE/Replo-V2-Architecture?node-id=212-180&t=HNVE1x9HPAgBwKUt-4
   */
  const onSubmit = async (data: DomainFormData) => {
    if (!entriToken || !applicationId || !shopDomainPrefix || !workspace) {
      return;
    }

    const domainValue = data.domain;

    logEvent("shop.domain.add");
    reset();

    const isReploshopCnameSetOnCustomDomain =
      await v2CustomDomainUtils.isReploshopCnameSetOnCustomDomain.fetch(
        {
          projectId,
          customDomain: domainValue,
        },
        {
          staleTime: 0, // Force fresh fetch every time
        },
      );

    if (isReploshopCnameSetOnCustomDomain) {
      createPendingDomain({
        projectId,
        customDomain: domainValue,
        workspaceId: workspace.id,
      });
    } else {
      showEntriModal(applicationId, entriToken, shopDomainPrefix, domainValue);
    }
  };

  const isSubmitDisabled =
    !entriToken ||
    !applicationId ||
    !isValid ||
    !shopDomainPrefix ||
    (!canAddCustomDomain && !hasPaidSubscription);

  const firstError = getFirstError(errors);

  const getDisabledReason = () => {
    if (!canAddCustomDomain && !hasPaidSubscription) {
      return "You've reached the custom domain limit for your workspace";
    }
    if (!entriToken || !applicationId) {
      return "Loading configuration...";
    }
    if (!shopDomainPrefix) {
      return "Shop domain prefix not configured";
    }
    return "Please enter a valid domain (e.g., example.com or shop.example.com)";
  };

  const disabledTooltip = isSubmitDisabled
    ? firstError || getDisabledReason()
    : undefined;

  return (
    <div className="flex flex-col gap-3 w-full">
      <DomainsDisplay
        projectId={projectId}
        shopDomainPrefix={shopDomainPrefix}
      />

      {/* Domain input form */}
      <form onSubmit={(e) => void handleSubmit(onSubmit)(e)}>
        <div className="flex flex-col w-full gap-3 py-3 rounded-lg">
          <div className="flex items-end justify-between w-full gap-3">
            <LabeledControl
              label="Domain"
              error={errors.domain?.message}
              size="base"
              className="flex-1"
              required
            >
              <Input
                size="base"
                type="text"
                placeholder="shop.example.com or example.com"
                maxLength={256}
                {...register("domain")}
                invalid={Boolean(errors.domain)}
              />
            </LabeledControl>
            <div className="flex gap-2">
              {showCancelButton && (
                <Button
                  variant="secondary"
                  size="base"
                  onClick={() => {
                    // Navigate back to general settings
                    window.history.back();
                  }}
                >
                  Cancel
                </Button>
              )}
              <Tooltip content={disabledTooltip} triggerAsChild>
                <Button
                  variant="primary"
                  size="base"
                  type="submit"
                  disabled={isSubmitDisabled}
                  loading={isEntriModalInProgress}
                >
                  Connect
                </Button>
              </Tooltip>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};
